/**
 * Pure helpers for `TokenTool`. Tests live in
 * `tests/web/tokenTool.test.ts`.
 *
 * Cryptographic randomness only — `Math.random()` is never used here.
 * `crypto.getRandomValues` is required by all evergreen browsers and
 * by Node 18+, so no fallback path.
 */

export type TokenEncoding = 'hex' | 'base64' | 'base64url';

const MIN_BYTES = 1;
const MAX_BYTES = 1024;
const MAX_COUNT = 1000;

/**
 * Generate one cryptographically random token of `bytes` bytes,
 * rendered as `encoding`.
 *
 * Throws on out-of-range `bytes` so callers can surface the message
 * directly in their error UI.
 */
export function generateToken(bytes: number, encoding: TokenEncoding): string {
  validateBytes(bytes);
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return encodeBytes(buf, encoding);
}

/** Generate `count` independent tokens. Used by the batch UI. */
export function generateTokens(
  bytes: number,
  encoding: TokenEncoding,
  count: number,
): string[] {
  validateBytes(bytes);
  validateCount(count);
  const out: string[] = new Array(count);
  for (let i = 0; i < count; i++) out[i] = generateToken(bytes, encoding);
  return out;
}

function validateBytes(bytes: number): void {
  if (!Number.isInteger(bytes) || bytes < MIN_BYTES || bytes > MAX_BYTES) {
    throw new Error(`字节数需为 ${MIN_BYTES} – ${MAX_BYTES} 的整数（实际：${bytes}）`);
  }
}

function validateCount(count: number): void {
  if (!Number.isInteger(count) || count < 1 || count > MAX_COUNT) {
    throw new Error(`数量需为 1 – ${MAX_COUNT} 的整数（实际：${count}）`);
  }
}

export function encodeBytes(buf: Uint8Array, encoding: TokenEncoding): string {
  switch (encoding) {
    case 'hex':
      return bytesToHex(buf);
    case 'base64':
      return bytesToBase64(buf, /* urlSafe */ false);
    case 'base64url':
      return bytesToBase64(buf, /* urlSafe */ true);
  }
}

function bytesToHex(buf: Uint8Array): string {
  let s = '';
  for (let i = 0; i < buf.length; i++) {
    s += buf[i].toString(16).padStart(2, '0');
  }
  return s;
}

function bytesToBase64(buf: Uint8Array, urlSafe: boolean): string {
  // `btoa` takes a binary string. Building it via String.fromCharCode +
  // chunked apply keeps us O(n) without blowing the call-stack on
  // 1024-byte inputs (the spread `...buf` form is fine here but the
  // chunked variant matches what other lib files in this codebase use
  // and stays correct for arbitrarily large buffers).
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < buf.length; i += chunk) {
    bin += String.fromCharCode.apply(null, Array.from(buf.subarray(i, i + chunk)));
  }
  const std = btoa(bin);
  if (!urlSafe) return std;
  return std.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export const TOKEN_LIMITS = {
  MIN_BYTES,
  MAX_BYTES,
  MAX_COUNT,
} as const;
